import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import SlideEditor from '~/client/components/slide-editor/components/SlideEditor'
import { useEditorStore } from '~/client/components/slide-editor/store'
import {
  getPresentation,
  isPresentationNotFoundError,
  updatePresentationSlides,
  updatePresentationName,
} from '~/client/components/present/common/presentationStorageRemote'
import type { GeneratedPresentation } from '~/client/components/present/common/templateDefinition'
import type { Slide } from '~/client/components/slide-editor/types'
import { NotFound } from '~/client/components/NotFound'

export const Route = createFileRoute(
  '/_app/presentation-editor/$presentationId',
)({
  component: PresentationEditorPage,
})

// ─── Auto-save debounce interval (ms) ───────────────────────────────────────
const AUTOSAVE_DELAY = 2000

function PresentationEditorPage() {
  const navigate = useNavigate()
  const { presentationId } = Route.useParams()

  const [presentation, setPresentation] =
    useState<GeneratedPresentation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [saveStatus, setSaveStatus] = useState<
    'saved' | 'saving' | 'unsaved' | 'error'
  >('saved')

  const loadSlides = useEditorStore((s) => s.loadSlides)
  const setPresentationTitle = useEditorStore((s) => s.setPresentationTitle)
  const setEventSlug = useEditorStore((s) => s.setEventSlug)
  const slides = useEditorStore((s) => s.slides)

  // Track whether we've done the initial load so we don't auto-save on mount
  const hasLoadedRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const presentationIdRef = useRef(presentationId)
  presentationIdRef.current = presentationId



  // ── Load presentation from storage on mount ───────────────────────────
  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)
      setNotFound(false)
      // Clear before each load so a failed/not-found load can't leave a stale
      // slug pointing at the previously-opened presentation's event.
      setEventSlug(null)

      try {
        const pres = await getPresentation(presentationId)

        if (cancelled) return

        if (!pres) {
          setNotFound(true)
          setLoading(false)
          return
        }

        setPresentation(pres)
        setPresentationTitle(pres.name || 'Untitled Presentation')
        setEventSlug(pres.eventSlug ?? null)

        if (pres.slides.length > 0) {
          loadSlides(pres.slides)
        }

        // Mark as loaded after a tick so the slide subscription doesn't
        // immediately trigger a save for the initial load
        requestAnimationFrame(() => {
          if (!cancelled) {
            hasLoadedRef.current = true
          }
        })
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load presentation:', err)
          if (isPresentationNotFoundError(err)) {
            setNotFound(true)
            return
          }
          setError('Failed to load presentation.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [presentationId, loadSlides, setPresentationTitle, setEventSlug])

  // ── Auto-save: subscribe to slide changes ─────────────────────────────
  const doSave = useCallback(async (slidesToSave: Slide[]) => {
    if (!presentationIdRef.current) return

    setSaveStatus('saving')
    try {
      await updatePresentationSlides(presentationIdRef.current, slidesToSave)
      setSaveStatus('saved')
    } catch (err) {
      console.error('Auto-save failed:', err)
      setSaveStatus('error')
    }
  }, [])

  useEffect(() => {
    // Don't save until the initial load is complete
    if (!hasLoadedRef.current) return

    setSaveStatus('unsaved')

    // Debounce the save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }

    saveTimerRef.current = setTimeout(() => {
      doSave(slides)
    }, AUTOSAVE_DELAY)

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
      }
    }
  }, [slides, doSave])

  // ── Flush any pending save on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        // Fire a final save synchronously-ish
        const currentSlides = useEditorStore.getState().slides
        updatePresentationSlides(
          presentationIdRef.current,
          currentSlides,
        ).catch((err) =>
          console.error('Final save on unmount failed:', err),
        )
      }
    }
  }, [])

  // ── Handle back / close ───────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (presentation?.eventSlug) {
      navigate({
        to: '/events/$eventSlug',
        params: { eventSlug: presentation.eventSlug },
        search: { tab: 'shortlist' },
      } as any)
    } else {
      navigate({ to: '/' })
    }
  }, [navigate, presentation?.eventSlug])

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-[#8A3DFF] rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading presentation…</p>
        </div>
      </div>
    )
  }

  if (notFound) {
    return <NotFound />
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-red-500"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <p className="text-gray-700 font-medium">{error}</p>
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  // ── Editor ────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Top bar with presentation name + save status + close */}
      <div className="h-10 bg-white border-b border-gray-200 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            title="Close editor"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <EditablePresentationName
            name={presentation?.name || 'Untitled Presentation'}
            onRename={(newName) => {
              if (presentation) {
                setPresentation({ ...presentation, name: newName })
                // Persist the title into the editor store so exports use it
                setPresentationTitle(newName)
                // Persist to backend storage
                updatePresentationName(presentationId, newName).catch((err) =>
                  console.error('Failed to save presentation name:', err),
                )
              }
            }}
          />
        </div>

        <div className="flex items-center gap-3">
          <SaveStatusIndicator status={saveStatus} />
        </div>
      </div>

      {/* Slide editor fills the rest */}
      <div className="flex-1 min-h-0">
        <SlideEditor />
      </div>
    </div>
  )
}

// ─── Save status indicator ──────────────────────────────────────────────────

// ─── Editable presentation name ─────────────────────────────────────────────

const EditablePresentationName: React.FC<{
  name: string
  onRename: (newName: string) => void
}> = ({ name, onRename }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setDraft(name)
  }, [name, isEditing])

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const commit = useCallback(() => {
    const next = draft.trim() || 'Untitled Presentation'
    if (next !== name) onRename(next)
    setDraft(next)
    setIsEditing(false)
  }, [draft, name, onRename])

  const cancel = useCallback(() => {
    setDraft(name)
    setIsEditing(false)
  }, [name])

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      {isEditing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            // Stop propagation so canvas shortcuts (Delete, tool keys, etc.) don't fire
            e.stopPropagation()
            if (e.key === 'Enter') { e.preventDefault(); commit() }
            if (e.key === 'Escape') { e.preventDefault(); cancel() }
          }}
          className="h-7 w-[260px] max-w-[40vw] rounded-md border border-[#8A3DFF]/30 bg-white px-2 text-sm font-medium text-gray-700 outline-none ring-2 ring-[#8A3DFF]/15"
          aria-label="Presentation name"
        />
      ) : (
        <span
          className="text-sm font-medium text-gray-700 truncate max-w-[400px] cursor-pointer hover:text-gray-900"
          title={name}
          onDoubleClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDraft(name)
            setIsEditing(true)
          }}
        >
          {name}
        </span>
      )}
      <button
        type="button"
        onMouseDown={(e) => {
          // Prevent default so the browser doesn't move focus to the button,
          // which would blur the input and cause commit before we
          // can decide whether to enter or exit editing mode.
          e.preventDefault()
          e.stopPropagation()
          if (isEditing) commit()
          else { setDraft(name); setIsEditing(true) }
        }}
        title={isEditing ? 'Save name' : 'Edit name'}
        className="inline-flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </button>
    </div>
  )
}

// ─── Save status indicator ──────────────────────────────────────────────────

const SaveStatusIndicator: React.FC<{
  status: 'saved' | 'saving' | 'unsaved' | 'error'
}> = ({ status }) => {
  const config = {
    saved: {
      label: 'Saved',
      dotClass: 'bg-green-400',
      textClass: 'text-gray-400',
    },
    saving: {
      label: 'Saving…',
      dotClass: 'bg-yellow-400 animate-pulse',
      textClass: 'text-gray-500',
    },
    unsaved: {
      label: 'Unsaved changes',
      dotClass: 'bg-yellow-400',
      textClass: 'text-gray-500',
    },
    error: {
      label: 'Save failed',
      dotClass: 'bg-red-400',
      textClass: 'text-red-500',
    },
  }

  const { label, dotClass, textClass } = config[status]

  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${dotClass}`} />
      <span className={`text-[11px] ${textClass}`}>{label}</span>
    </div>
  )
}
